<template>
    <TprmModal
        open
        title="Entity Already Exists"
        :allow-close="true"
        @close="$emit('cancel')"
    >
        This email address <b>{{ contactEmail }}</b> is already associated with an existing entity.
        You can't create a new entity with an email address that is already in use.
        Instead, assign the risk profile to the existing entity.
        <template #footer>
            <NewButton
                variant="outline"
                color="neutral"
                @click="$emit('cancel')"
            >
                Cancel
            </NewButton>
            <NewButton
                variant="fill"
                color="primary"
                :loading="isAssigning"
                @click="assignToExistingEntity"
            >
                Assign to Existing Entity
            </NewButton>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { ActionResolveInput } from '@evidentid/tprm-portal-lib/models/entity-actions-review';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import {
        extractConflictEmail,
        transformToExistingEntityResolve,
    } from '@/modules/contract-extraction/utils/contractExtractionConflictUtils';

    export default defineComponent({
        name: 'EntityAlreadyExistsModal',
        components: {
            NewButton,
            TprmModal,
        },
        props: {
            rpName: {
                type: String,
                required: true,
            },
            resolveData: {
                type: Object as () => ActionResolveInput,
                required: true,
            },
        },
        emits: [ 'cancel', 'error', 'resolve' ],
        data() {
            return {
                isAssigning: false,
            };
        },
        computed: {
            contactEmail(): string {
                return extractConflictEmail(this.resolveData) || '';
            },
        },
        methods: {
            async assignToExistingEntity(): Promise<void> {
                this.isAssigning = true;
                try {
                    const result = await (this as any).$gatewayClient.tprmUi().getTprmEntities(this.rpName, {
                        contactEmail: this.contactEmail,
                        limit: 1,
                    });
                    const entity = result.insureds?.[0];
                    if (!entity) {
                        this.$emit('error', 'Could not find existing entity with that email address');
                        return;
                    }
                    const modifiedData = transformToExistingEntityResolve(this.resolveData, entity.id);
                    this.$emit('resolve', modifiedData);
                } catch {
                    this.$emit('error', 'Failed to look up existing entity');
                } finally {
                    this.isAssigning = false;
                }
            },
        },
    });
</script>
